import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { ActionsPage } from "./features/actions/ActionsPage";
import { LoginPage } from "./features/auth/LoginPage";
import { MfaPage } from "./features/auth/MfaPage";
import { AppLayout } from "./features/layout/AppLayout";
import { MembersPage } from "./features/members/MembersPage";
import { OrganizationsPage } from "./features/organizations/OrganizationsPage";
import { ApiRequestError } from "./lib/api";
import { meQuery } from "./lib/me";
import { Toaster } from "./ui/toast";

// Code-based routes: five screens do not justify route code generation (KISS).

// The action view carries MapLibre and terra-draw (most of the bundle); load it on demand.
const ActionPage = lazy(() =>
  import("./features/action/ActionPage").then((m) => ({ default: m.ActionPage })),
);

// The read-only view for other services (issue #163): no staff session, the link token decides.
const SharedViewPage = lazy(() =>
  import("./features/shared-view/SharedViewPage").then((m) => ({ default: m.SharedViewPage })),
);

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const sharedViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/podglad",
  component: () => (
    <Suspense fallback={null}>
      <SharedViewPage />
    </Suspense>
  ),
});

/** Everything behind the staff session. MFA setup is forced for admins who lack it. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      if (me.mfaRequired && location.pathname !== "/mfa") throw redirect({ to: "/mfa" });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401)
        throw redirect({ to: "/login" });
      throw error;
    }
  },
  component: AppLayout,
});

const actionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: ActionsPage,
});
const mfaRoute = createRoute({ getParentRoute: () => appRoute, path: "/mfa", component: MfaPage });
const actionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/actions/$actionId",
  component: function ActionRouteComponent() {
    const { actionId } = actionRoute.useParams();
    // key: a fresh live state per action
    return (
      <Suspense fallback={null}>
        <ActionPage key={actionId} actionId={actionId} />
      </Suspense>
    );
  },
});
const membersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organizations/$organizationId/members",
  component: function MembersRouteComponent() {
    const { organizationId } = membersRoute.useParams();
    return <MembersPage organizationId={organizationId} />;
  },
});
const organizationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/organizations",
  component: OrganizationsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  sharedViewRoute,
  appRoute.addChildren([actionsRoute, mfaRoute, actionRoute, membersRoute, organizationsRoute]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: false });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
